import {
  FintracFormFeatureFlagConditional,
  FintracFormField,
  FintracFormFieldConditional,
  FintracFormFieldSection,
  FintracFormValueOrArray,
} from 'app/modules/fintracStr/models';
import { FeatureFlags } from 'app/shared/featureFlags/models';
import { isArray, isPlainObject, isUndefined } from 'lodash';

export const isFeatureFlagEnabled = (
  featureFlag: FintracFormFeatureFlagConditional,
  featureFlags: FeatureFlags,
): boolean => {
  if (typeof featureFlag === 'string') {
    return !!featureFlags[featureFlag];
  }

  if ('and' in featureFlag) {
    return featureFlag.and.every((flag) => !!featureFlags[flag]);
  }

  return featureFlag.or.some((flag) => !!featureFlags[flag]);
};

export const isTabHidden = (
  field: FintracFormField | FintracFormField[] | FintracFormFieldSection,
  flattenedValues: Record<string, any>,
): boolean => {
  if (Array.isArray(field)) {
    return field.some(
      (f) => !(f.hideIfTrue && isFieldHidden(f.hideIfTrue, flattenedValues)),
    );
  }

  if (field.type === 'section') {
    return field.fields.some((f) => isTabHidden(f, flattenedValues));
  }

  return !(
    field.hideIfTrue && isFieldHidden(field.hideIfTrue, flattenedValues)
  );
};

export const isFieldHidden = (
  hideIfTrue: FintracFormFieldConditional,
  flattenedValues: Record<string, any>,
  currSectionIndex?: number,
  currFieldSectionIndex?: number[],
): boolean => {
  if (
    hideIfTrue.and?.length &&
    hideIfTrue.and.every((condition) => {
      let path = '';

      if (typeof condition.path === 'function') {
        // We assume that path is empty if path is a function and currSectionIndex is undefined
        path = isUndefined(currSectionIndex)
          ? ''
          : condition.path(currSectionIndex, currFieldSectionIndex);
      } else {
        path = condition.path;
      }

      // If path is empty, we assume that the field should be hidden
      return path === '' ? true : condition.conditions(flattenedValues[path]);
    })
  ) {
    return true;
  }

  if (
    hideIfTrue.or &&
    hideIfTrue.or.some((condition) => {
      let path = '';

      if (typeof condition.path === 'function') {
        // We assume that path is empty if path is a function and currSectionIndex is undefined
        path = isUndefined(currSectionIndex)
          ? ''
          : condition.path(currSectionIndex, currFieldSectionIndex);
      } else {
        path = condition.path;
      }

      // If path is empty, we assume that the field should be hidden
      return path === '' ? true : condition.conditions(flattenedValues[path]);
    })
  ) {
    return true;
  }

  return false;
};

export const isFieldBlank = (value: FintracFormValueOrArray) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (isArray(value) && value.length === 0) ||
    (isPlainObject(value) && Object.keys(value).length === 0)
  );
};

export const isFalse = (value: FintracFormValueOrArray): boolean =>
  Boolean(value) === false;
